/**
 * sceneSelection — which scene object the transform gizmo is attached to, and
 * in what mode. A module-level singleton with subscribe/notify, mirroring
 * liveTuneStore / graphState / debugState.
 *
 * Deliberately NOT React state: the gizmo reads this from inside the R3F tree
 * and the outliner reads it from the DOM tree, and neither should force the
 * other to re-render. Same rule as every other runtime singleton here.
 *
 * Nothing is persisted. Selection is ephemeral session state (like the camera
 * cage gizmo flag) — reopening the app should not silently re-attach a gizmo
 * to an object you no longer have in view.
 *
 * Exposed as window.__SCENE_SELECTION__ for tests and console use.
 */

export const MODES = ['translate', 'rotate', 'scale']
export const SPACES = ['world', 'local']

const DEFAULTS = Object.freeze({
  // Adapter id from transformAdapters, or null for "nothing selected"
  // (the gizmo unmounts entirely — it is never rendered detached).
  selectedId: null,
  mode: 'translate',
  space: 'world',
  // null = no snapping on that channel. Units: metres / degrees / ratio.
  snap: Object.freeze({ translate: null, rotate: null, scale: null }),
})

let state = clone(DEFAULTS)
const listeners = new Set()

function clone(s) {
  return { ...s, snap: { ...s.snap } }
}

function notify() {
  if (typeof window !== 'undefined') window.__SCENE_SELECTION__ = sceneSelection
  for (const fn of listeners) fn()
}

export const sceneSelection = {
  getState() { return state },

  /**
   * Shallow patch, with `snap` merged one level down so a caller can set a
   * single channel without clobbering the others.
   */
  setState(patch = {}) {
    const next = { ...state, ...patch }
    if (patch.snap) next.snap = { ...state.snap, ...patch.snap }
    // Reject unknown enum values rather than letting a typo put the gizmo in
    // an undefined mode (TransformControls would throw deep in its own code).
    if (patch.mode !== undefined && !MODES.includes(patch.mode)) return false
    if (patch.space !== undefined && !SPACES.includes(patch.space)) return false
    state = next
    notify()
    return true
  },

  select(id) { return this.setState({ selectedId: id ?? null }) },
  clear() { return this.setState({ selectedId: null }) },

  subscribe(fn) {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },

  reset() {
    state = clone(DEFAULTS)
    notify()
  },
}

// Publish immediately so a test/console can reach it before the first edit.
if (typeof window !== 'undefined') window.__SCENE_SELECTION__ = sceneSelection
